import React from "react";
import { Link } from "react-router-dom";
import { PlayCircle, Lock, Clock, Award } from "lucide-react";

const DIFFICULTY_TONE = {
  Beginner: "text-[#10B981] border-[#10B981]/30",
  Intermediate: "text-[#3B82F6] border-[#3B82F6]/30",
  Advanced: "text-[#F59E0B] border-[#F59E0B]/30",
  Expert: "text-[#FF4343] border-[#FF4343]/30",
};

export default function DashboardCourseCard({ course, locked, ctaLabel }) {
  const tone = DIFFICULTY_TONE[course.difficulty] || "text-gray-600 border-gray-300";
  const progressPct = Math.max(0, Math.min(100, course.progress_percent || 0));
  const label =
    ctaLabel ||
    (progressPct === 0 ? "Start" : progressPct === 100 ? "Review" : "Continue");

  const link = locked
    ? "/pricing"
    : `/learn/${course.slug}`;

  return (
    <Link
      to={link}
      className={`course-card group relative flex flex-col bg-white border ${
        locked ? "border-gray-200" : "border-gray-100"
      } overflow-hidden`}
      data-testid={`dashboard-course-card-${course.slug}`}
    >
      {locked && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-[2px]">
          <div className="text-center px-4">
            <Lock className="mx-auto text-fc-red mb-3" size={22} />
            <p className="text-fc-red font-black text-sm uppercase tracking-wide mb-1">Executive Only</p>
            <p className="text-gray-500 text-xs mb-4">Upgrade to unlock this course.</p>
            <span className="btn-fc-red text-[10px] py-2 px-4">Upgrade</span>
          </div>
        </div>
      )}

      <div className="relative aspect-[16/10] overflow-hidden bg-fc-navy">
        <img src={course.thumbnail_url} alt={course.title} loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060A24] via-[#060A24]/40 to-transparent" />
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-2 text-[10px] tracking-[0.18em] font-semibold">
          <span className={`px-2 py-0.5 border ${tone}`}>{course.difficulty.toUpperCase()}</span>
          <span className="text-gray-500 flex items-center gap-1"><Clock size={10} /> {course.estimated_duration_hours}h</span>
        </div>
        <h3 className="text-gray-900 font-black text-base leading-tight tracking-tight mb-2 group-hover:text-fc-red transition-colors">{course.title}</h3>
        <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 flex-1">{course.subtitle}</p>

        {!locked && (
          <>
            <div className="mt-4 h-1.5 w-full bg-gray-200 overflow-hidden rounded-full">
              <div
                className="h-full bg-fc-red transition-all duration-700"
                style={{ width: `${progressPct}%` }}
                data-testid={`progress-bar-${course.slug}`}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] tracking-[0.18em] uppercase font-bold">
              <span className="text-gray-500">{course.lessons_completed || 0}/{course.total_lessons || 0} lessons</span>
              <span className="text-fc-red flex items-center gap-1.5">
                <PlayCircle size={12} /> {label}
              </span>
            </div>
          </>
        )}
      </div>
    </Link>
  );
}
